import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { Analytics, getAnalytics } from 'firebase/analytics';
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import { Auth } from 'firebase/auth';
import { RoleType, SignInProvider } from '@type/authType';
import { FirebaseAppAuthStore } from './firebaseAppAuthStore';
import { FirebaseWebAuthStore } from './firebaseWebAuthStore';

/**
 * 서비스별 Firebase 설정
 * - 각 앱의 진입점에서 initialize() 호출 시 주입
 */
export type FirebaseServiceConfig = {
    dev: FirebaseOptions;
    prod: FirebaseOptions;
};

export type TokenUserData = {
    hash: string;
    roleType: RoleType | null;
};

export interface FirebaseAuthStore {
    /**
     * 로그인 진행 중 상태
     */
    isLoggingIn: boolean;

    /**
     * 로그인 중인 메서드
     */
    loginMethod: SignInProvider | null;

    /**
     * 사용자 정보
     */
    me: TokenUserData | null;

    loginWithGoogle(): Promise<void>;

    loginWithApple(): Promise<void>;

    loginWithCode(code: string): Promise<void>;

    getIdToken(): Promise<string | null>;

    updateMe(): Promise<void>;

    refreshToken(): Promise<void>;

    saveFcmTokenToServer(): Promise<void>;

    logout(): Promise<void>;
}

class FirebaseStore {
    firebaseApp: FirebaseApp | null = null;
    firebaseAuth: Auth | null = null;
    firebaseAnalytics: Analytics | null = null;

    /**
     * Firebase 인증 정보 스토어
     * - 웹과 앱의 인증 처리 방식이 다르다
     * - 앱에서는 웹뷰 브릿지를 통해 앱과 웹뷰 간 인증 정보를 유지하도록 한다
     */
    firebaseAuthStore: FirebaseAuthStore | null = null;

    /**
     * Firebase 초기화 여부
     */
    isInitialized = false;

    /**
     * API 에서 인증 헤더를 보내기 때문에 Firebase 인증 정보가 초기화될 때까지 기다리기 위한 Promise
     */
    authReadyPromise: Promise<void> | null = null;
    authReadyResolve: (() => void) | null = null;

    constructor() {
        makeObservable(this, {
            firebaseAuthStore: observable,
            isInitialized: observable,

            isLoggingIn: computed,
            loginMethod: computed,
            me: computed,

            initialize: action,
        });

        this.authReadyPromise = new Promise((resolve) => {
            this.authReadyResolve = () => {
                runInAction(() => {
                    this.isInitialized = true;
                    resolve();
                });
            };
        });
    }

    get isLoggingIn() {
        return this.firebaseAuthStore?.isLoggingIn || false;
    }

    get loginMethod() {
        return this.firebaseAuthStore?.loginMethod || null;
    }

    /**
     * 토큰에 저장된 사용자 정보
     * - 스토어가 초기화되지 않은 시점에서는 항상 null 임
     * - 컴포넌트에서 me 를 확인해 업데이트 하려면 isInitialized 와 함께 확인해줘야 함
     * - 로그인 여부를 확실히 기다려야 할 때는 isLoggedIn 을 사용해야 함
     */
    get me(): TokenUserData | null {
        return this.firebaseAuthStore?.me || null;
    }

    get isAdmin() {
        return this.me?.roleType === 'admin';
    }

    /**
     * 로그인 여부
     * - 스토어가 초기화되기를 기다렸다가 응답한다
     */
    async isLoggedIn(): Promise<boolean> {
        await this.waitUntilAuthReady();
        return !!this.me;
    }

    initialize(isWebView: boolean, config: FirebaseServiceConfig) {
        const firebaseConfig = import.meta.env.PROD ? config.prod : config.dev;
        this.firebaseApp = initializeApp(firebaseConfig);
        this.firebaseAnalytics = getAnalytics(this.firebaseApp);

        if (isWebView) {
            this.firebaseAuthStore = new FirebaseAppAuthStore(this.authReadyResolve!);
        } else {
            this.firebaseAuthStore = new FirebaseWebAuthStore(
                this.firebaseApp,
                this.authReadyResolve!,
            );
        }
    }

    /**
     * 이미 가지고 있는 토큰으로 사용자 정보를 갱신함
     */
    async updateMe() {
        await this.firebaseAuthStore?.updateMe();
    }

    /**
     * Firebase 인증 정보가 초기화될 때까지 기다리기 위한 Promise
     */
    async waitUntilAuthReady(): Promise<void> {
        await this.authReadyPromise;
    }

    async loginWithGoogle() {
        await this.firebaseAuthStore?.loginWithGoogle();
    }

    async loginWithApple() {
        await this.firebaseAuthStore?.loginWithApple();
    }

    async loginWithCode(code: string) {
        await this.firebaseAuthStore?.loginWithCode(code);
    }

    async getIdToken() {
        return this.firebaseAuthStore?.getIdToken() || null;
    }

    async refreshToken() {
        await this.firebaseAuthStore?.refreshToken();
    }

    async logout() {
        await this.firebaseAuthStore?.logout();
    }
}

export default new FirebaseStore();
